import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { toastMessage } from "../../../constants/strings";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import {
  osadAssignmentSchema,
  osadShipmentsSchema,
  type OsadAssignment,
} from "../../../types/operations/osad.type";
import { http } from "../../../utils/httpCommon";
import { toast } from "../../../utils/snackbarHelper";
import { safeParsePartial } from "../../../utils/zodSafeParse";
import { useApiUrls } from "../../useApiUrl";

interface UseOSADArgs {
  sicId: number;
  onSuccess: (successInfo: {
    affectedShipmentsCount: number;
    shipmentIds: number[];
  }) => void;
  onSettled?: () => void;
}

export const useOSAD = ({ sicId, onSuccess, onSettled }: UseOSADArgs) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { osad } = hookUrls;

  return useMutation({
    mutationFn: async (request: OsadAssignment) => {
      const payload = osadAssignmentSchema.parse(request);

      const url = osad.createOSAD(sicId);
      const { status } = await http.post(url, payload);
      return status;
    },
    onSuccess: (_, variables) => {
      const uniqueShipmentIds = new Set(variables.shipmentIds);

      if (variables.shipmentIds.length > 0) {
        const affectedShipmentsCount = uniqueShipmentIds.size;

        onSuccess({
          affectedShipmentsCount,
          shipmentIds: variables.shipmentIds,
        });
      }
    },
    onError: (error: AxiosError) => {
      if (error.response) {
        toast(error.response.data?.toString(), { variant: "error" });
      } else {
        toast(toastMessage.generics.error, { variant: "error" });
      }
    },
    onSettled: () => {
      // Invalidating all queries because the list was long and complex: https://github.com/daylighttransport/operations-iol-webapp/pull/1370/files
      queryClient.invalidateQueries();
      onSettled?.();
    },
  });
};

export const osadKeys = {
  shipments: (sicId: number, planDate?: string) =>
    ["osad", "shipments", sicId, planDate] as const,
  allShipmentsForSic: (sicId: number) => ["osad", "shipments", sicId] as const,
};

export function useOSADShipments(
  sicId: number,
  enabled = true,
  planDate?: string
) {
  const hookUrls = useApiUrls();
  const { osad } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    refetchOnMount: false,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: osadKeys.shipments(sicId, planDate),
    queryFn: async () => {
      const url = osad.getOsadDetails(sicId);
      const { data } = await http.get(url, {
        params: { ...(planDate && { planDate }) },
      });
      return isSafeParseEnabled
        ? safeParsePartial(osadShipmentsSchema, data, "useOSADShipments")
        : osadShipmentsSchema.parse(data);
    },
    enabled,
  });
}
